import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { formatModifier } from '@/utils/statMeta';
import { cn } from '@/lib/utils';

// atom — DWR score chip per SectionStats click-based pool e stat tile.
// 4 variant:
//  - 'pool'          : 48×48 + border ink 2px + bg paper-bright + mod chip
//                      bottom-right (.score-chip + .score-chip__mod del mockup)
//  - 'pool-disabled' : come pool ma opacity 35% + border dashed (.score-chip--placed)
//  - 'selected'      : come pool ma ring offset 2px ink + scale 1.05 + bg paper-soft
//                      (FI-54: pattern click-based, chip "active" in attesa di target stat)
//  - 'assigned'      : 56×48 + bg ink + text paper-bright + no border + no mod
//                      (.stat-row__score del mockup — il mod è nello slot stat esterno)
// Pattern .score-chip mockup (styles.css 584-605) + .stat-row__score (642-657).
//
// **FI-54 (PR feature/fi-54-stat-assignment-mobile-ux)**: rimosso drag&drop.
// Il chip è button click-based. `forwardRef` mantenuto per backward-compat
// e per integrazione con eventuali pattern futuri (es. focus management).
//
// near-duplicate of future <Chip variant>: emergerà quando in PR 7/8 si toccano
// stat-chip / meta-chip / spell-tag. Per ora atom dedicato a SectionStats.

export type ScoreChipVariant = 'pool' | 'pool-disabled' | 'selected' | 'assigned';

interface ScoreChipProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  score: number;
  variant?: ScoreChipVariant;
  mod?: number | undefined;
}

export const ScoreChip = forwardRef<HTMLButtonElement, ScoreChipProps>(function ScoreChip(
  { score, variant = 'pool', mod, className, type, ...rest },
  ref,
) {
  const showMod = (variant === 'pool' || variant === 'selected') && mod !== undefined;

  return (
    <button
      ref={ref}
      type={type ?? 'button'}
      className={cn(
        'font-display relative grid place-items-center text-[1.2rem] font-bold select-none transition-[transform,box-shadow] duration-150',
        variantClasses(variant),
        className,
      )}
      {...rest}
    >
      {score}
      {showMod ? (
        <span
          aria-hidden
          className="bg-ink text-paper-bright font-display absolute -right-[10px] -bottom-[10px] px-[0.35em] py-[0.15em] text-[0.7rem]"
        >
          {formatModifier(mod)}
        </span>
      ) : null}
    </button>
  );
});

function variantClasses(variant: ScoreChipVariant): string {
  if (variant === 'pool') {
    return 'border-ink bg-paper-bright h-12 w-12 cursor-pointer border-2 hover:bg-paper-soft';
  }
  if (variant === 'pool-disabled') {
    return 'border-ink bg-paper-bright h-12 w-12 cursor-default border-2 border-dashed opacity-35';
  }
  if (variant === 'selected') {
    // ring 2px ink-strong offset + scale 1.05 + bg paper-soft = active state
    // visibile pre-tap stat (memoria feedback_mobile_testing_required: feedback
    // immediato di selezione su touch).
    return 'border-ink bg-paper-soft h-12 w-12 cursor-pointer border-2 scale-105 shadow-[0_0_0_2px_var(--color-ink-soft)]';
  }
  // assigned
  return 'bg-ink text-paper-bright h-12 w-14 cursor-pointer text-[1.5rem] border-0 hover:bg-accent-dwr';
}
